import './polyfills/node-browser-polyfills'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './styles/print.css'
import { ThemeProvider } from './contexts/ThemeContext'
import { HelmetProvider } from 'react-helmet-async'

// Auto-reload on stale chunk errors (after a new deploy invalidates cached JS chunks)
const RELOAD_FLAG = 'bd_chunk_reload_count';
const RELOAD_TIME = 'bd_chunk_reload_time';
const isChunkLoadError = (msg?: string) =>
  !!msg && /Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk [\d]+ failed|error loading dynamically imported module/i.test(msg);
const tryReload = () => {
  try {
    const count = Number(sessionStorage.getItem(RELOAD_FLAG) || '0');
    const last = Number(sessionStorage.getItem(RELOAD_TIME) || '0');
    // Reset counter if last reload was >60s ago
    const newCount = Date.now() - last > 60_000 ? 1 : count + 1;
    if (newCount > 5) return; // give up after 5 attempts to avoid infinite loop
    sessionStorage.setItem(RELOAD_FLAG, String(newCount));
    sessionStorage.setItem(RELOAD_TIME, String(Date.now()));
  } catch {}
  // Bypass cache by appending timestamp + strip any stale ?_r param
  const url = new URL(window.location.href);
  url.searchParams.delete('_r');
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
};
window.addEventListener('error', (e) => {
  if (isChunkLoadError(e?.message) || isChunkLoadError((e as any)?.error?.message)) tryReload();
});
window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
  const reason: any = e?.reason;
  const msg = typeof reason === 'string' ? reason : reason?.message;
  if (isChunkLoadError(msg)) tryReload();
});


const onRenderError = (err: unknown) => {
  const msg = (err as any)?.message || String(err);
  if (isChunkLoadError(msg)) {
    tryReload();
    return;
  }
  console.error('Render error:', err);
};

try {
  createRoot(document.getElementById("root")!, {
    // React 19 hooks — harmless on React 18
    onUncaughtError: onRenderError,
    onCaughtError: onRenderError,
  } as any).render(
    <HelmetProvider>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </HelmetProvider>
  );
  // Reset stale-chunk reload counter once the app has mounted successfully,
  // so future stale-chunk events after a new deploy can recover cleanly.
  requestAnimationFrame(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
      sessionStorage.removeItem(RELOAD_TIME);
    } catch {}
  });
} catch (err) {
  onRenderError(err);
}
